import { useState, useCallback } from "react";
import { Activity, RefreshCw, Radio } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";
import { useHealthSyncMonitoring } from "../hooks/useHealthSyncMonitoring";
import type { SyncHealthStatus, HealthSyncNudgeStats } from "../types";
import {
  SYNC_HEALTH_STATUSES,
  STATUS_COLOR,
  STAT_CARD_COLOR,
  PROVIDERS,
} from "../constants";

// ─── Stats Bar ─────────────────────────────────────────────────────────────

function HealthSyncStatsBar({
  stats,
  loading,
}: {
  stats?: {
    totalDevices: number;
    healthyCount: number;
    warningCount: number;
    criticalCount: number;
    neverSyncedCount: number;
    disconnectedCount: number;
  };
  loading: boolean;
}) {
  const { t } = useLocale();

  const cards = [
    { labelKey: "healthSync.stats.total", value: stats?.totalDevices ?? 0, color: "text-foreground" },
    { labelKey: "healthSync.stats.healthy", value: stats?.healthyCount ?? 0, color: STAT_CARD_COLOR.healthy },
    { labelKey: "healthSync.stats.warning", value: stats?.warningCount ?? 0, color: STAT_CARD_COLOR.warning },
    { labelKey: "healthSync.stats.critical", value: stats?.criticalCount ?? 0, color: STAT_CARD_COLOR.critical },
    { labelKey: "healthSync.stats.neverSynced", value: stats?.neverSyncedCount ?? 0, color: STAT_CARD_COLOR.neverSynced },
    { labelKey: "healthSync.stats.disconnected", value: stats?.disconnectedCount ?? 0, color: STAT_CARD_COLOR.disconnected },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      {cards.map((card) => (
        <div key={card.labelKey} className="rounded-lg border border-border bg-card p-4">
          <p className="text-xs font-medium text-muted-foreground">{t(card.labelKey)}</p>
          <p className={`mt-1 text-2xl font-bold ${card.color}`}>
            {loading ? "…" : card.value}
          </p>
        </div>
      ))}
    </div>
  );
}

// ─── Table ──────────────────────────────────────────────────────────────────

function NudgeStatsCard({
  nudgeStats,
}: {
  nudgeStats: HealthSyncNudgeStats | null | undefined;
}) {
  const { t } = useLocale();

  if (!nudgeStats) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
        <Radio className="h-4 w-4" />
        {t("healthSync.nudge.noData")}
      </div>
    );
  }

  const lastNudgeAt = nudgeStats.lastNudgeAt
    ? new Date(nudgeStats.lastNudgeAt).toLocaleString()
    : "—";
  const failRate =
    nudgeStats.totalTokens > 0
      ? ((nudgeStats.failed / nudgeStats.totalTokens) * 100).toFixed(1)
      : "0";

  return (
    <div className="flex flex-wrap items-center gap-4 rounded-lg border border-border bg-card p-4 text-sm">
      <div className="flex items-center gap-2 font-medium text-foreground">
        <Radio className="h-4 w-4" />
        {t("healthSync.nudge.title")}
      </div>
      <span className="text-muted-foreground">
        {t("healthSync.nudge.lastSent")}: <span className="text-foreground">{lastNudgeAt}</span>
      </span>
      <span className="text-muted-foreground">
        {t("healthSync.nudge.tokens")}: <span className="text-foreground">{nudgeStats.totalTokens}</span>
      </span>
      <span className="text-muted-foreground">
        {t("healthSync.nudge.sent")}: <span className="text-green-600">{nudgeStats.sent}</span>
      </span>
      <span className="text-muted-foreground">
        {t("healthSync.nudge.failed")}: <span className={nudgeStats.failed > 0 ? "text-red-500" : "text-foreground"}>{nudgeStats.failed}</span>
      </span>
      <span className="text-muted-foreground">
        {t("healthSync.nudge.failRate")}: <span className={Number(failRate) > 10 ? "text-red-500" : "text-foreground"}>{failRate}%</span>
      </span>
      <span className="text-muted-foreground">
        {t("healthSync.nudge.duration")}: <span className="text-foreground">{nudgeStats.durationMs}ms</span>
      </span>
      {nudgeStats.failedUsers.length > 0 && (
        <div className="mt-2 w-full border-t border-border pt-2">
          <p className="mb-1 text-xs font-medium text-red-500">
            {t("healthSync.nudge.failedUsersTitle")} ({nudgeStats.failedUsers.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {nudgeStats.failedUsers.map((fu) => (
              <span
                key={fu.userId}
                className="inline-flex items-center gap-1 rounded bg-red-50 px-2 py-0.5 text-xs text-red-700"
              >
                <span className="font-mono select-all">{fu.userId}…</span>
                <span className="text-red-400">({fu.errorType})</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function HealthSyncTable({
  devices,
  loading,
}: {
  devices: {
    userId: string;
    provider: string;
    connected: boolean;
    syncEnabled: boolean;
    lastSyncAt: string | null;
    status: SyncHealthStatus;
    hoursSinceLastSync: number;
  }[];
  loading: boolean;
}) {
  const { t } = useLocale();

  function formatLastSync(lastSyncAt: string | null): string {
    if (!lastSyncAt) return "—";
    return new Date(lastSyncAt).toLocaleString();
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-border bg-muted/50 text-xs font-medium uppercase text-muted-foreground">
          <tr>
            <th className="px-4 py-3">{t("healthSync.table.userId")}</th>
            <th className="px-4 py-3">{t("healthSync.table.provider")}</th>
            <th className="px-4 py-3">{t("healthSync.table.status")}</th>
            <th className="px-4 py-3">{t("healthSync.table.lastSync")}</th>
            <th className="px-4 py-3">{t("healthSync.table.hoursSince")}</th>
            <th className="px-4 py-3">{t("healthSync.table.connected")}</th>
            <th className="px-4 py-3">{t("healthSync.table.syncEnabled")}</th>
          </tr>
        </thead>
        <tbody>
          {loading && devices.length === 0 ? (
            <tr>
              <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                {t("common.loading")}
              </td>
            </tr>
          ) : devices.length === 0 ? (
            <tr>
              <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                {t("healthSync.noDevices")}
              </td>
            </tr>
          ) : (
            devices.map((device, idx) => (
              <tr
                key={`${device.userId}-${device.provider}-${idx}`}
                className="border-b border-border last:border-0 hover:bg-muted/30"
              >
                <td className="px-4 py-3 font-mono text-xs select-all">
                  {device.userId}
                </td>
                <td className="px-4 py-3">{device.provider}</td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLOR[device.status]}`}
                  >
                    {t(`healthSync.status.${device.status}`)}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs">{formatLastSync(device.lastSyncAt)}</td>
                <td className="px-4 py-3 tabular-nums">
                  {device.hoursSinceLastSync >= 0 ? device.hoursSinceLastSync : "—"}
                </td>
                <td className="px-4 py-3">
                  <span className={device.connected ? "text-green-600" : "text-red-500"}>
                    {device.connected ? "✓" : "✗"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={device.syncEnabled ? "text-green-600" : "text-red-500"}>
                    {device.syncEnabled ? "✓" : "✗"}
                  </span>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

export function HealthSyncMonitoringPage() {
  const { t } = useLocale();

  const [statusFilter, setStatusFilter] = useState<SyncHealthStatus | "ALL">("ALL");
  const [providerFilter, setProviderFilter] = useState<string>("ALL");
  const [page, setPage] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const { data, loading, refetch } = useHealthSyncMonitoring({
    status: statusFilter !== "ALL" ? statusFilter : undefined,
    provider: providerFilter !== "ALL" ? providerFilter : undefined,
    first: PAGE_SIZE,
    skip: page * PAGE_SIZE,
  });

  const payload = data?.adminHealthSyncMonitoring;
  const devices = payload?.devices ?? [];
  const stats = payload?.stats;
  const totalCount = payload?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const isRefreshing = loading || refreshing;

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refetch();
    } finally {
      setTimeout(() => setRefreshing(false), 600);
    }
  }, [refetch]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
            <Activity className="h-7 w-7" />
            {t("healthSync.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("healthSync.subtitle")}
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
          {isRefreshing ? t("dashboard.refreshing") : t("common.refresh")}
        </button>
      </div>

      {/* Stats */}
      <HealthSyncStatsBar stats={stats} loading={loading && !stats} />

      {/* Silent Push Nudge Stats */}
      <NudgeStatsCard nudgeStats={payload?.lastNudgeStats} />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as SyncHealthStatus | "ALL");
            setPage(0);
          }}
          className="h-9 rounded-md border border-border bg-background px-3 text-sm"
        >
          <option value="ALL">{t("healthSync.filter.allStatuses")}</option>
          {SYNC_HEALTH_STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`healthSync.status.${s}`)}
            </option>
          ))}
        </select>

        <select
          value={providerFilter}
          onChange={(e) => {
            setProviderFilter(e.target.value);
            setPage(0);
          }}
          className="h-9 rounded-md border border-border bg-background px-3 text-sm"
        >
          <option value="ALL">{t("healthSync.filter.allProviders")}</option>
          {PROVIDERS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>

        <span className="ml-auto text-sm text-muted-foreground">
          {totalCount} {t("healthSync.deviceCount")}
        </span>
      </div>

      {/* Table */}
      <HealthSyncTable devices={devices} loading={loading} />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            {t("common.showingRange", {
              start: String(page * PAGE_SIZE + 1),
              end: String(Math.min((page + 1) * PAGE_SIZE, totalCount)),
              total: String(totalCount),
            })}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="rounded-md border border-border px-3 py-1 text-sm disabled:opacity-50"
            >
              {t("common.previousPage")}
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="rounded-md border border-border px-3 py-1 text-sm disabled:opacity-50"
            >
              {t("common.nextPage")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
